import { MenuItem } from "@material-ui/core";
import { styled } from "@shoothill/core";
import { useObserver } from "mobx-react-lite";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { PrimaryButton, DefaultButton } from "Components/Buttons/Buttons";
import { InductionAdminEditView } from "./Edit/InductionAdminEditView";
import { InductionAdminEditViewModel } from "./Edit/InductionAdminEditViewModel";

import { DeleteModal } from "Components/Modal/DeleteModal";
import DeleteLogo from "Content/Bin.svg";
import PictureIcon from "Content/picture.svg";
import { DownloadFileIcon } from "Content/DownloadFileIcon";
import { InductionType } from "Globals/Models/Enums/InductionType";
import { DashedDivider, SolidDivider } from "Views/PurchaseOrder/Form/Views/Dividers";
import { InductionPersonalProtectiveEquipmentDTO, InductionQualificationDTO } from "./InductionAdminItemModel";
import { InductionAdminItemViewModel } from "./InductionAdminItemViewModel";
import { InductionPhotoEvidenceModal } from "./InductionPhotoEvidenceModal";
import {
    AnswerTile,
    AnswerTileContainer,
    CustomSelect,
    InductionDetailsPageContainer,
    InductionItemRow,
    InductionItemRows,
    IsApprovedSection,
    ItemDetailsContainer,
    ItemDetailsSection,
    ItemDetailsSectionRow,
    QualificationTable,
    SignatureSection,
    ToggleButton,
    VideoResponseTile,
    VideoResponseTileContainer,
} from "./InductionAdminDetailsView.styles";
import { InductionAdminViewModel } from "../InductionAdminViewModel";
import { isEvidenceAnImage } from "Utils/Utils";
import { SupplierModel } from "Views/PurchaseOrder/Form/Supporting/SupplierModel";

interface IProps {
    className?: string;
    viewModel: InductionAdminItemViewModel;
    suppliers: SupplierModel[];
}

interface PhotoEvidence {
    id: string | null;
    qualificationName: string;
    photoUrl: string;
}

const InductionAdminDetailsViewBase: React.FC<IProps> = (props) => {
    const { viewModel } = props;
    const [activeItemId, setActiveItemId] = React.useState<string | null>(null);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [photoEvidence, setPhotoEvidence] = React.useState<PhotoEvidence | null>(null);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [editViewModel] = React.useState(() => new InductionAdminEditViewModel(props.suppliers));
    const [isTemporarilyDisabled, setIsTemporarilyDisabled] = useState(false);

    // #region Code Behind

    const [color, setColor] = useState<string>("");
    const [backgroundColor, setBackgroundColor] = useState<string>("");

    const openEvidenceModal = (evidence: PhotoEvidence) => {
        setPhotoEvidence(evidence);
        setIsOpen(true);
    };

    const closeEvidenceModal = () => {
        setPhotoEvidence(null);
        setIsOpen(false);
    };

    const handleOnDownloadPhotoEvidence = (event: any) => {
        event.stopPropagation();
        if (photoEvidence && photoEvidence.photoUrl) {
            const tempLink = document.createElement("a");
            tempLink.href = photoEvidence.photoUrl;
            const fileName = `${photoEvidence.qualificationName || "photo-evidence"}.jpg`;
            tempLink.download = fileName;
            tempLink.setAttribute("download", fileName);
            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
        }
    };

    const handleDeleteClose = () => {
        setDeleteOpen(false);
    };

    const handleDeleteInduction = () => {
        viewModel.deleteInduction();
        setDeleteOpen(false);
    };

    useEffect(() => {
        if (viewModel.inductionAdminViewModelExists) {
            const currentStatus = InductionAdminViewModel.GetInstance.inductionStatuses.find(
                (s) => s.id === InductionAdminViewModel.GetInstance.inductionAdminItemViewModel.model.inductionStatusId,
            );
            if (currentStatus) {
                setColor(currentStatus.textColor);
                setBackgroundColor(currentStatus.color);
            }
        }
    }, []);

    useEffect(() => {
        if (editViewModel) {
            editViewModel.suppliers = props.suppliers;
        }
    }, [props.suppliers]);

    const onSelect = async (value: any) => {
        setIsTemporarilyDisabled(true); // Disable for 5 seconds

        const newStatus = InductionAdminViewModel.GetInstance.inductionStatuses.find((s) => s.id === value);

        if (newStatus) {
            setColor(newStatus.textColor);
            setBackgroundColor(newStatus.color);
            InductionAdminViewModel.GetInstance.inductionAdminItemViewModel.model.setValue("inductionStatusId", value);
            await viewModel.updateInductionStatus(value);
        }

        setTimeout(() => {
            setIsTemporarilyDisabled(false); // Re-enable after 5 seconds
        }, 5000);
    };

    const getPassedValue = (val: boolean | null) => {
        if (val === null) {
            return <span>N/A</span>;
        } else if (val === true) {
            return <span className="red">Y</span>;
        } else {
            return <span>N</span>;
        }
    };

    const renderQualificationsTable = (items: InductionQualificationDTO[], nameTitle: string, isDefault: boolean = false) => {
        return (
            <QualificationTable>
                <div className="columns">
                    <div className="column">{nameTitle}</div>
                    <div className="column">Yes/No</div>
                    <div className="column">Type/Level</div>
                    {!isDefault && <div className="column">ID/Roll number</div>}
                    {!isDefault && <div className="column">Expiry date</div>}
                    {!isDefault && <div className="column">Evidence</div>}
                </div>
                <div className="rows">
                    {items.map((i) => {
                        return (
                            <div className="row">
                                <div className="cell">{i.name}</div>
                                <div className="cell">{getPassedValue(i.passed)}</div>
                                <div className="cell">{i.typeLevel}</div>
                                {!isDefault && <div className="cell">{i.idRollNumber}</div>}
                                {!isDefault && <div className="cell">{i.expiryDate ? moment(i.expiryDate).format("DD/MM/YYYY") : ""}</div>}
                                {!isDefault && (
                                    <div className="cell">
                                        {i.photoEvidenceUrl && i.photoEvidenceUrl !== "" && isEvidenceAnImage(i.photoEvidenceUrl) && (
                                            <img
                                                style={{ cursor: "pointer" }}
                                                src={PictureIcon}
                                                onClick={() => openEvidenceModal({ id: i.id, qualificationName: i.name, photoUrl: i.photoEvidenceUrl })}
                                            />
                                        )}
                                        {i.photoEvidenceUrlInline && i.photoEvidenceUrlInline !== "" && !isEvidenceAnImage(i.photoEvidenceUrlInline) && (
                                            <DownloadFileIcon style={{ cursor: "pointer" }} onClick={() => viewModel.OpenFile(i.photoEvidenceUrlInline)} />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </QualificationTable>
        );
    };

    const renderPersonalProtectiveEquipmentTable = (items: InductionPersonalProtectiveEquipmentDTO[]) => {
        return (
            <QualificationTable>
                <div className="columns">
                    <div className="column">Personal Protective Equipment Group</div>
                    <div className="column-autowidth">Equipment</div>
                </div>
                <div className="rows">
                    {items.map((i) => {
                        return (
                            <div className="row" key={i.id}>
                                <div className="cell">{i.displayName}</div>
                                <div className="cell-autowidth">{i.types}</div>
                            </div>
                        );
                    })}
                </div>
            </QualificationTable>
        );
    };

    const handleEditClick = () => {
        // Copy data from view model to edit model
        editViewModel.model.fromDto(viewModel.model);
        // Initialize the list view models
        editViewModel.initializeFromModel();
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const renderItemDetails = (item: InductionAdminItemViewModel) => {
        return (
            <ItemDetailsContainer>
                {isEditing ? (
                    <>
                        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                            <DefaultButton displayName="Cancel" execute={handleCancelEdit} canExecute={true} fullWidth={false} />
                        </div>
                        <InductionAdminEditView viewModel={editViewModel} />
                    </>
                ) : (
                    <>
                        <DashedDivider gutterBottom={true} borderThickness={2} borderOpacity={0.5} />
                        {/* Age */}
                        <ItemDetailsSection>
                            <ItemDetailsSectionRow>
                                <p style={{ marginRight: "170px" }}>
                                    <span>Age:</span> {item.model.age}
                                </p>
                                <p>
                                    <span>Over 18:</span> {item.confirmOver18Formatted}
                                </p>
                            </ItemDetailsSectionRow>
                            <p>
                                <span>Please provide any adjustments need to be made for you based on your age: </span> {item.confirmAdjustmentsFormatted}
                            </p>
                        </ItemDetailsSection>
                        <DashedDivider gutterBottom={true} borderThickness={2} borderOpacity={0.5} />
                        {/* Health problems */}
                        <ItemDetailsSection>
                            <p>
                                <span>Do you have any health problems that could affect your ability to carry out your tasks safely?</span> {item.healthProblemsFormatted}
                            </p>
                            <AnswerTileContainer>
                                <p>
                                    <span>Details: </span>
                                </p>
                                <AnswerTile>
                                    <p>{item.model.healthDetails}</p>
                                </AnswerTile>
                            </AnswerTileContainer>
                        </ItemDetailsSection>
                        <SolidDivider gutterBottom />
                        {/* Skills/Qualifications, Plants/Equipment and Personal Protective Equipment */}
                        {(() => {
                            switch (true) {
                                case viewModel.inductionType?.IsOfType(InductionType.Full):
                                    return (
                                        <ItemDetailsSection>
                                            <p>
                                                <span>What Skills & Training/Qualifications do you have?</span>
                                            </p>
                                            {renderQualificationsTable(viewModel.model.skillsTrainingOrQualifications, "Skills & training/qualification")}
                                            <p>
                                                <span>What Plant & Equipment Training/Qualifications do you have?</span>
                                            </p>
                                            {renderQualificationsTable(viewModel.model.plantAndEquipmentTrainingOrQualifications, "Plant & Equipment Training/Qualifications")}
                                            <p>
                                                <span>What Personal Protective Equipment have you brought with you?</span>
                                            </p>
                                            {renderPersonalProtectiveEquipmentTable(viewModel.model.personalProtectiveEquipmentView)}
                                        </ItemDetailsSection>
                                    );

                                default:
                                    return <React.Fragment />;
                            }
                        })()}
                        {/* Video confirmation */}
                        <ItemDetailsSection>
                            <p>
                                <span>Induction video watched, you hereby fully confirm and accept, without exception, that you:</span>
                            </p>
                            {/* General */}
                            <VideoResponseTileContainer>
                                <p>
                                    <span>General</span>
                                </p>
                                <VideoResponseTile>
                                    <div>
                                        <p>Fully understood the video induction and all messages contained within the video.</p>
                                        <p>Know where to find a full copy of the Darwin Group Health and Safety Policy.</p>
                                        <p>Have read and understood your own company Health and Safety Policy.</p>
                                        <p>Will adhere to all site rules and instructions relating to health, safety and housekeeping from site management/supervision.</p>
                                    </div>
                                    <div>{item.isGeneralConfirmedFormatted}</div>
                                </VideoResponseTile>
                            </VideoResponseTileContainer>
                            {/* PPE */}
                            <VideoResponseTileContainer>
                                <p>
                                    <span>PPE</span>
                                </p>
                                <VideoResponseTile>
                                    <div>
                                        <p>Fully understand the Personal Protective Equipment rules and requirements of the site.</p>
                                        <p>Fully understand the Personal Protective Equipment requirements for all tasks that you are undertaking.</p>
                                        <p>
                                            Have the correct Personal Protective Equipment, in good working order and free of defect or fault, required to undertake your tasks or
                                            visit the site safely.
                                        </p>
                                    </div>
                                    <div>{item.isPPEConfirmedFormatted}</div>
                                </VideoResponseTile>
                            </VideoResponseTileContainer>
                            {/* Safety */}
                            <VideoResponseTileContainer>
                                <p>
                                    <span>Safety</span>
                                </p>
                                <VideoResponseTile>
                                    <div>
                                        <p>
                                            Understand and accept that under no circumstances will you access the site without permanent escort from the Darwin Group site
                                            management team.
                                        </p>
                                        <p>
                                            Understand and accept that, at certain parts of the construction period, certain areas of the site will be deemed unsafe for visitors
                                            and access will be prohibited to those areas.
                                        </p>
                                        <p>Understand and accept that during site tours, you will ensure you are with the site management team at all times during the visit.</p>
                                        <p>Understand and accept that you must obey all instructions given by the site management team at all times during the visit.</p>
                                    </div>
                                    <div>{item.isSafetyConfirmedFormatted}</div>
                                </VideoResponseTile>
                            </VideoResponseTileContainer>
                            {/* Housekeeping */}
                            <VideoResponseTileContainer>
                                <p>
                                    <span>Housekeeping</span>
                                </p>
                                <VideoResponseTile>
                                    <div>
                                        <p>Understand and accept the housekeeping standards and the requirement for daily housekeeping.</p>
                                        <p>Understand and accept the Clean Up Notice protocols operated by Darwin Group should you not adhere to housekeeping standards.</p>
                                        <p>Understand the environmental impact of chemical spillages and understand the correct emergency spill response procedures.</p>
                                        <p>Will dispose of waste in the correct way at all times.</p>
                                        <p>Will not burn any waste, under any circumstances.</p>
                                    </div>
                                    <div>{item.isHousekeepingConfirmedFormatted}</div>
                                </VideoResponseTile>
                            </VideoResponseTileContainer>
                            {/* Rules/Discipline */}
                            <VideoResponseTileContainer>
                                <p>
                                    <span>Rules/Discipline</span>
                                </p>
                                <VideoResponseTile>
                                    <div>
                                        <p>Fully understand the discipline expected from you whilst working on a Darwin Group site.</p>
                                        <p>Fully understand and accept the red/yellow card system operated on Darwin Group sites.</p>
                                        <p>Fully understand and accept that bullying/harassment of any kind will not be tolerated and the reporting procedures.</p>
                                        <p>Will not smoke, vape, or otherwise use e-cigarettes anywhere other than designated smoking areas or outside of the site boundaries.</p>
                                        <p>Understand and will adhere to the rules on mobile phones and electronic devices, portable radios, entertainment systems etc.</p>
                                    </div>
                                    <div>{item.isRulesOrDisciplineConfirmedFormatted}</div>
                                </VideoResponseTile>
                            </VideoResponseTileContainer>
                            {/* Site Specific Briefing */}
                            <VideoResponseTileContainer>
                                <p>
                                    <span>Site Specific Briefing</span>
                                </p>
                                <VideoResponseTile>
                                    <div>
                                        <p>Will report to the Site Manager for a site specific briefing prior to entering the site for the first time.</p>
                                        <p>
                                            Will not, <div style={{ display: "inline", textDecoration: "underline" }}>under any circumstances,</div> enter site for the first time
                                            without having received this briefing.
                                        </p>
                                    </div>
                                    <div>{item.isRulesOrDisciplineConfirmedFormatted}</div>
                                </VideoResponseTile>
                            </VideoResponseTileContainer>
                        </ItemDetailsSection>
                        <SolidDivider gutterBottom />
                        {viewModel.hasSignature && (
                            <>
                                <SignatureSection>
                                    <p>
                                        <span>Signed by {viewModel.model.fullName.toUpperCase()}</span>
                                    </p>
                                    <div>
                                        <img src={viewModel.model.signatureURL} alt="User signature" />
                                    </div>
                                </SignatureSection>
                                <SolidDivider gutterBottom />
                            </>
                        )}
                        <IsApprovedSection>
                            <p>
                                <span>{viewModel.isApprovedStringFormatted}</span>
                            </p>
                        </IsApprovedSection>
                    </>
                )}
            </ItemDetailsContainer>
        );
    };

    const renderItems = () => {
        return (
            <InductionItemRows>
                <div>
                    <InductionItemRow>
                        <div>
                            <span style={{ color: "#0095DA" }}>{viewModel.model.visitorTypeName}</span>
                            {activeItemId === viewModel.model.id ? (
                                <ToggleButton>
                                    <span
                                        className={"open"}
                                        onClick={() => {
                                            setActiveItemId(null);
                                        }}
                                    >
                                        &minus;
                                    </span>
                                </ToggleButton>
                            ) : (
                                <ToggleButton>
                                    <span
                                        className={"opcloseen"}
                                        onClick={() => {
                                            setActiveItemId(viewModel.model.id);
                                        }}
                                    >
                                        &#43;
                                    </span>
                                </ToggleButton>
                            )}
                        </div>
                        <div>
                            <strong>Submitted:</strong> {viewModel.createdAtFormatted}
                        </div>
                        <div>
                            <CustomSelect
                                value={viewModel.getCurrentStatusId}
                                onChange={(event) => (onSelect ? onSelect(event.target.value) : {})}
                                disabled={
                                    viewModel.IsLoading ||
                                    (viewModel.inductionAdminViewModelExists ? InductionAdminViewModel.GetInstance.IsLoading : false) ||
                                    isTemporarilyDisabled
                                }
                                backgroundColor={backgroundColor}
                                textColor={color}
                            >
                                {viewModel.inductionAdminViewModelExists &&
                                    InductionAdminViewModel.GetInstance.inductionStatusOptions?.map((item: any, index: number) => {
                                        return (
                                            <MenuItem key={index} value={item.value}>
                                                {item.label}
                                            </MenuItem>
                                        );
                                    })}
                            </CustomSelect>
                        </div>
                        <div>
                            <PrimaryButton displayName="Edit" execute={handleEditClick} canExecute={!isEditing} fullWidth={false} />
                        </div>
                        <div style={{ cursor: "pointer" }} className="icon" onClick={() => setDeleteOpen(true)}>
                            <img src={DeleteLogo} />
                        </div>
                    </InductionItemRow>
                    {activeItemId === viewModel.model.id && renderItemDetails(viewModel)}
                    <DashedDivider gutterBottom={true} borderThickness={2} borderOpacity={0.5} />
                </div>
            </InductionItemRows>
        );
    };

    const renderPage = () => {
        return (
            <InductionDetailsPageContainer>
                {renderItems()}
                <DeleteModal
                    open={deleteOpen}
                    onClose={handleDeleteClose}
                    onDelete={handleDeleteInduction}
                    title="Delete induction?"
                    text="Are you sure you want to delete this induction?"
                    isLoading={viewModel.IsLoading}
                />
                <InductionPhotoEvidenceModal
                    open={isOpen}
                    onClose={closeEvidenceModal}
                    title={`Evidence - ${photoEvidence?.qualificationName}`}
                    onDownload={handleOnDownloadPhotoEvidence}
                >
                    {isEvidenceAnImage(photoEvidence?.photoUrl) && (
                        <div className="modal-photoevidence">
                            <div className="modal-photoevidence-body">
                                <div className="photoEvidence-container">
                                    <img src={photoEvidence?.photoUrl} />
                                </div>
                            </div>
                        </div>
                    )}
                </InductionPhotoEvidenceModal>
            </InductionDetailsPageContainer>
        );
    };

    return useObserver(() => renderPage());
};

export const InductionAdminDetailsView = styled(InductionAdminDetailsViewBase)``;
